import { useState } from "react";
import { useToast } from "@/components/base/Toast";
import { fetchApi } from "@/services/apiService";

type Props = {
  open: boolean;
  onClose: () => void;
  quoteId?: number | string;
  quoteNo: string;
  leadName: string;
  leadEmail: string;
  totalFormatted: string;
  validUntil: string;
  agent: string;
  companyName: string;
  leadId?: number | string;
};

export function SendQuotationModal({
  open,
  onClose,
  quoteId,
  quoteNo,
  leadName,
  leadEmail,
  totalFormatted,
  validUntil,
  agent,
  companyName,
  leadId
}: Props) {
  const { showToast } = useToast();
  const [to, setTo] = useState(leadEmail);
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(`Quotation ${quoteNo} from ${companyName}`);
  const [message, setMessage] = useState(
    `Dear ${leadName ? leadName.split(" ")[0] : "Client"},\n\nPlease find attached your quotation ${quoteNo} for ${totalFormatted}.\n\nThis quotation is valid until ${validUntil || "30 days"}.\n\nFor any queries please contact us.\n\nRegards,\n${agent}\n${companyName}`
  );
  const [markSent, setMarkSent] = useState(true);
  const [logActivity, setLogActivity] = useState(true);
  const [sendCopy, setSendCopy] = useState(false);
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const handleSend = async () => {
    if (!to.trim()) {
      showToast("Please specify a recipient email address", "error");
      return;
    }

    setSending(true);
    try {
      if (quoteId && markSent) {
        await fetchApi(`/quotations/${quoteId}/status`, {
          method: "PATCH",
          body: JSON.stringify({ status: "Sent" }),
        }).catch(() => {});
      }

      if (leadId && logActivity) {
        await fetchApi(`/leads/${leadId}/activities`, {
          method: "POST",
          body: JSON.stringify({
            type: "Quotation Sent",
            description: `Quotation ${quoteNo} for ${totalFormatted} was sent to ${to}`,
            performed_by: agent || "System",
          }),
        }).catch(() => {});
      }

      showToast(`Quotation sent to ${to}`, "success");
      onClose();
    } catch (err: any) {
      showToast(err.message || "Failed to send quotation", "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />
        <div className="relative bg-white rounded-xl border border-gray-200 shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto z-10">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-xl z-10">
            <h2 className="text-base font-semibold text-gray-900">Send Quotation to Lead</h2>
            <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
              <i className="ri-close-line" />
            </button>
          </div>

          {/* Body */}
          <div className="p-5 space-y-4">
            {/* To */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To Email</label>
              <input
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="client@example.com"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 outline-none transition-all"
              />
            </div>

            {/* CC */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">CC (optional)</label>
              <input
                type="text"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="Add CC recipients..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 outline-none transition-all"
              />
            </div>

            {/* Subject */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 outline-none transition-all"
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 outline-none transition-all resize-none"
              />
            </div>

            {/* Attachment */}
            <div className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 rounded-lg">
              <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-file-pdf-line text-red-500 text-base" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700 truncate">{quoteNo}-{leadName ? leadName.replace(/\s/g, "") : "Quotation"}.pdf</p>
              </div>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[10px] font-semibold whitespace-nowrap">
                <i className="ri-check-line text-[10px]" />
                Auto-attached
              </span>
            </div>

            {/* Send Options */}
            <div className="space-y-2.5 bg-gray-50 rounded-lg p-3">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={markSent}
                  onChange={(e) => setMarkSent(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-400 cursor-pointer"
                />
                <span className="text-sm text-gray-700">Mark quotation as "Sent" after send</span>
              </label>
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={logActivity}
                  onChange={(e) => setLogActivity(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-400 cursor-pointer"
                />
                <span className="text-sm text-gray-700">Log activity on lead timeline</span>
              </label>
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendCopy}
                  onChange={(e) => setSendCopy(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-400 cursor-pointer"
                />
                <span className="text-sm text-gray-700">Send me a copy</span>
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
            <button
              onClick={onClose}
              disabled={sending}
              className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending}
              className="px-5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              {sending ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className="ri-send-plane-line" />}
              Send Quotation
            </button>
          </div>
        </div>
      </div>
    </>
  );
}